import { useState, useRef, useCallback } from "react";

// ── CDN LIBRARIES (loaded dynamically) ───────────────────────────────────────
const CDN = {
  pdfjs:      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js",
  pdfjsWorker:"https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js",
  jspdf:      "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js",
  mammoth:    "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js",
  docx:       "https://cdn.jsdelivr.net/npm/docx@8.5.0/build/index.umd.js",
  xlsx:       "https://cdn.jsdelivr.net/npm/xlsx/dist/xlsx.full.min.js",
};

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}

// ── EXCEL COLUMN DATA TYPES ───────────────────────────────────────────────────
const TYPE_DEFS = [
  { id: "text",       label: "Text",        icon: "Aa",  color: "#94a3b8" },
  { id: "number",     label: "Number",      icon: "123", color: "#3b8ef8" },
  { id: "float",      label: "Decimal",     icon: "1.5", color: "#8b5cf6" },
  { id: "date",       label: "Date",        icon: "D",   color: "#22c55e" },
  { id: "datetime",   label: "Datetime",    icon: "DT",  color: "#f59e0b" },
  { id: "time",       label: "Time",        icon: "T",   color: "#06b6d4" },
  { id: "boolean",    label: "Boolean",     icon: "T/F", color: "#ef4444" },
  { id: "percentage", label: "Percentage",  icon: "%",   color: "#F05A41" },
  { id: "currency",   label: "Currency",    icon: "$",   color: "#D8A056" },
  { id: "email",      label: "Email",       icon: "@",   color: "#ec4899" },
  { id: "url",        label: "URL",         icon: "🔗",  color: "#14b8a6" },
  { id: "trim",       label: "Trim spaces", icon: "⌁",   color: "#a3e635" },
  { id: "upper",      label: "UPPERCASE",   icon: "AA",  color: "#fb923c" },
  { id: "lower",      label: "lowercase",   icon: "aa",  color: "#a78bfa" },
  { id: "title",      label: "Title Case",  icon: "Aa_", color: "#34d399" },
];

function transformValue(val, toType) {
  const str = String(val ?? "").trim();
  switch (toType) {
    case "text":   return str;
    case "trim":   return str;
    case "upper":  return str.toUpperCase();
    case "lower":  return str.toLowerCase();
    case "title":  return str.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    case "number": {
      const n = parseInt(str.replace(/[^0-9.-]/g, ""), 10);
      return isNaN(n) ? str : String(n);
    }
    case "float": {
      const f = parseFloat(str.replace(/[^0-9.-]/g, ""));
      return isNaN(f) ? str : f.toFixed(2);
    }
    case "date": {
      // Try natural date string first
      const d = new Date(str);
      if (!isNaN(d.getTime()) && str.length > 3) return d.toISOString().split("T")[0];
      // Try Excel serial number (days since Dec 30 1899)
      const serial = parseFloat(str);
      if (!isNaN(serial) && serial > 1) {
        const d2 = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
        return d2.toISOString().split("T")[0];
      }
      return str;
    }
    case "datetime": {
      const d = new Date(str);
      return !isNaN(d.getTime()) && str.length > 3
        ? d.toISOString().replace("T", " ").slice(0, 19)
        : str;
    }
    case "time": {
      const d = new Date(`1970-01-01T${str}`);
      if (!isNaN(d.getTime())) return d.toTimeString().slice(0, 8);
      const d2 = new Date(str);
      return !isNaN(d2.getTime()) ? d2.toTimeString().slice(0, 8) : str;
    }
    case "boolean": {
      const low = str.toLowerCase();
      if (["true","yes","1","y","on","✓","x"].includes(low))  return "TRUE";
      if (["false","no","0","n","off","✗",""].includes(low)) return "FALSE";
      const n = parseFloat(str);
      if (!isNaN(n)) return n !== 0 ? "TRUE" : "FALSE";
      return str.length > 0 ? "TRUE" : "FALSE";
    }
    case "percentage": {
      if (str.endsWith("%")) return str;
      const f = parseFloat(str.replace(/[^0-9.-]/g, ""));
      if (isNaN(f)) return str;
      return Math.abs(f) <= 1 ? `${(f * 100).toFixed(2)}%` : `${f.toFixed(2)}%`;
    }
    case "currency": {
      const f = parseFloat(str.replace(/[^0-9.-]/g, ""));
      return isNaN(f) ? str : `$${f.toFixed(2)}`;
    }
    case "email": return str.toLowerCase().replace(/\s/g, "");
    case "url": {
      if (!str) return str;
      if (/^https?:\/\//i.test(str)) return str;
      return `https://${str}`;
    }
    default: return str;
  }
}

// ── CONVERSION MODES ─────────────────────────────────────────────────────────
const MODES = [
  {
    id:    "img-to-pdf",
    label: "Images → PDF",
    icon:  "🖼",
    from:  "JPG / PNG / WEBP",
    to:    "PDF",
    accept:"image/jpeg,image/png,image/webp",
    multi: true,
    hint:  "Upload one or multiple images. They'll be placed one per page in a PDF.",
    quality:"Pixel-perfect",
    tag:   "pill-green",
  },
  {
    id:    "pdf-to-img",
    label: "PDF → Images",
    icon:  "📄",
    from:  "PDF",
    to:    "PNG images",
    accept:"application/pdf",
    multi: false,
    hint:  "Each page of the PDF is rendered as a high-quality PNG image.",
    quality:"High quality",
    tag:   "pill-blue",
  },
  {
    id:    "pdf-to-word",
    label: "PDF → Word",
    icon:  "📝",
    from:  "PDF",
    to:    "DOCX",
    accept:"application/pdf",
    multi: false,
    hint:  "Extracts text from each page and builds a Word document. Best for text-based PDFs.",
    quality:"Text only",
    tag:   "pill-amber",
  },
  {
    id:    "word-to-pdf",
    label: "Word → PDF",
    icon:  "📃",
    from:  "DOCX",
    to:    "PDF",
    accept:".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    multi: false,
    hint:  "Converts a Word document to a styled PDF. Best for text-heavy documents.",
    quality:"Good for text",
    tag:   "pill-blue",
  },
];

// ── HELPERS ───────────────────────────────────────────────────────────────────
function formatBytes(bytes) {
  if (bytes < 1024)      return `${bytes} B`;
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1024/1024).toFixed(1)} MB`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
}

function stripExt(name) { return name.replace(/\.[^/.]+$/, ""); }

// ── CONVERSIONS ───────────────────────────────────────────────────────────────

async function imagesToPdf(files, onProgress) {
  await loadScript(CDN.jspdf);
  const { jsPDF } = window.jspdf;
  let pdf = null;

  for (let i = 0; i < files.length; i++) {
    onProgress(`Processing image ${i + 1} of ${files.length}…`);
    const file = files[i];
    const imgData = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = e => res(e.target.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im); im.onerror = rej; im.src = imgData;
    });

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const orientation = w > h ? "landscape" : "portrait";
    const ptW = w * 0.75;
    const ptH = h * 0.75;

    if (!pdf) {
      pdf = new jsPDF({ orientation, unit: "pt", format: [ptW, ptH] });
    } else {
      pdf.addPage([ptW, ptH], orientation);
    }
    pdf.addImage(imgData, "JPEG", 0, 0, ptW, ptH, undefined, "FAST");
  }

  onProgress("Finalizing PDF…");
  const pdfBytes = pdf.output("arraybuffer");
  return new Blob([pdfBytes], { type: "application/pdf" });
}

async function pdfToImages(file, onProgress) {
  await loadScript(CDN.pdfjs);
  const pdfjsLib = window["pdfjs-dist/build/pdf"];
  pdfjsLib.GlobalWorkerOptions.workerSrc = CDN.pdfjsWorker;

  onProgress("Loading PDF…");
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const total = pdf.numPages;
  const blobs = [];

  for (let i = 1; i <= total; i++) {
    onProgress(`Rendering page ${i} of ${total}…`);
    const page     = await pdf.getPage(i);
    const scale    = 2.0;
    const viewport = page.getViewport({ scale });
    const canvas   = document.createElement("canvas");
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;
    const ctx      = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    const blob = await new Promise(res => canvas.toBlob(res, "image/png"));
    blobs.push({ blob, page: i });
  }

  onProgress("Done!");
  return blobs;
}

async function pdfToWord(file, onProgress) {
  await loadScript(CDN.pdfjs);
  await loadScript(CDN.docx);

  const pdfjsLib = window["pdfjs-dist/build/pdf"];
  pdfjsLib.GlobalWorkerOptions.workerSrc = CDN.pdfjsWorker;

  onProgress("Loading PDF…");
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const total = pdf.numPages;
  const paragraphs = [];

  for (let i = 1; i <= total; i++) {
    onProgress(`Extracting text from page ${i} of ${total}…`);
    const page    = await pdf.getPage(i);
    const content = await page.getTextContent();
    const lines   = {};

    for (const item of content.items) {
      const y = Math.round(item.transform[5]);
      if (!lines[y]) lines[y] = [];
      lines[y].push(item.str);
    }

    const sortedYs = Object.keys(lines).map(Number).sort((a, b) => b - a);
    for (const y of sortedYs) {
      const text = lines[y].join(" ").trim();
      if (text) {
        paragraphs.push(new window.docx.Paragraph({
          children: [new window.docx.TextRun({ text, size: 24 })],
          spacing: { after: 120 },
        }));
      }
    }

    if (i < total) {
      paragraphs.push(new window.docx.Paragraph({
        children: [],
        pageBreakBefore: true,
      }));
    }
  }

  onProgress("Building Word document…");
  const doc = new window.docx.Document({
    sections: [{ properties: {}, children: paragraphs }],
  });

  const buffer = await window.docx.Packer.toBlob(doc);
  onProgress("Done!");
  return buffer;
}

async function wordToPdf(file, onProgress) {
  await loadScript(CDN.mammoth);
  await loadScript(CDN.jspdf);

  onProgress("Reading Word document…");
  const arrayBuffer = await file.arrayBuffer();
  const result = await window.mammoth.convertToHtml({ arrayBuffer });
  const html = result.value;

  onProgress("Rendering content…");
  const { jsPDF } = window.jspdf;
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const doc = new DOMParser().parseFromString(html, "text/html");
  const div = doc.body;

  const margin = 20;
  const pageH  = 297;
  const pageW  = 210;
  const maxW   = pageW - margin * 2;
  let   y      = margin;
  const lineH  = 7;

  function addText(text, fontSize, bold) {
    pdf.setFontSize(fontSize);
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    const lines = pdf.splitTextToSize(text.trim(), maxW);
    for (const line of lines) {
      if (y + lineH > pageH - margin) { pdf.addPage(); y = margin; }
      pdf.text(line, margin, y);
      y += lineH;
    }
    y += 2;
  }

  const nodes = Array.from(div.childNodes);
  let count = 0;
  for (const node of nodes) {
    count++;
    if (count % 10 === 0) onProgress(`Rendering paragraph ${count} of ${nodes.length}…`);
    const tag  = node.nodeName?.toLowerCase();
    const text = node.textContent || "";
    if (!text.trim()) continue;
    if      (tag === "h1") addText(text, 20, true);
    else if (tag === "h2") addText(text, 16, true);
    else if (tag === "h3") addText(text, 14, true);
    else if (tag === "ul" || tag === "ol") {
      for (const li of Array.from(node.querySelectorAll("li"))) {
        addText(`• ${li.textContent}`, 11, false);
      }
    }
    else addText(text, 11, false);
  }

  onProgress("Finalizing PDF…");
  const pdfBytes = pdf.output("arraybuffer");
  return new Blob([pdfBytes], { type: "application/pdf" });
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
const TABS = ["Converter", "Excel Editor", "History"];

export default function FileConverter({ t, onExport }) {
  // ── Converter state ──
  const [activeTab, setActiveTab] = useState("Converter");
  const [mode, setMode]           = useState(MODES[0]);
  const [files, setFiles]         = useState([]);
  const [dragOver, setDragOver]   = useState(false);
  const [status, setStatus]       = useState("idle");
  const [progress, setProgress]   = useState("");
  const [results, setResults]     = useState([]);
  const [history, setHistory]     = useState([]);
  const [errorMsg, setErrorMsg]   = useState("");
  const fileInputRef = useRef(null);

  // ── Excel Editor state ──
  const [xlFile, setXlFile]         = useState(null);
  const [xlSheets, setXlSheets]     = useState([]);
  const [xlSheet, setXlSheet]       = useState("");
  const [xlData, setXlData]         = useState([]);   // 2-D: row 0 = headers
  const [xlFilters, setXlFilters]   = useState({});
  const [xlSort, setXlSort]         = useState({ col: -1, dir: "asc" });
  const [xlStatus, setXlStatus]     = useState("idle");
  const [xlDrag, setXlDrag]         = useState(false);
  const [xlEditCell, setXlEditCell] = useState(null); // { row, col }
  const [xlEditVal, setXlEditVal]   = useState("");
  const [xlSelected, setXlSelected] = useState(new Set());
  const [xlError, setXlError]       = useState("");
  const [xlColTypes, setXlColTypes] = useState({}); // col index → type id
  const xlFileRef  = useRef(null);
  const xlInputRef = useRef(null);
  const xlWorkbook = useRef(null);   // SheetJS workbook (non-reactive)

  // ── Converter callbacks ──────────────────────────────────────────────────────
  const resetState = useCallback(() => {
    setFiles([]); setStatus("idle"); setProgress(""); setResults([]); setErrorMsg("");
  }, []);

  const switchMode = (m) => { setMode(m); resetState(); };

  const handleFiles = (incoming) => {
    const arr = Array.from(incoming);
    if (!mode.multi && arr.length > 1) setFiles([arr[0]]);
    else setFiles(arr);
    setStatus("idle"); setResults([]); setErrorMsg("");
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const convert = useCallback(async () => {
    if (!files.length) return;
    setStatus("loading"); setResults([]); setErrorMsg("");

    try {
      const name = stripExt(files[0].name);

      if (mode.id === "img-to-pdf") {
        setProgress("Starting…");
        const blob = await imagesToPdf(files, setProgress);
        const outName = files.length === 1 ? `${name}.pdf` : `images-combined.pdf`;
        setResults([{ name: outName, blob, type: "pdf" }]);

      } else if (mode.id === "pdf-to-img") {
        setProgress("Starting…");
        const pages = await pdfToImages(files[0], setProgress);
        const res = pages.map(({ blob, page }) => ({
          name: `${name}-page-${String(page).padStart(3, "0")}.png`,
          blob,
          type: "png",
        }));
        setResults(res);

      } else if (mode.id === "pdf-to-word") {
        setProgress("Starting…");
        const blob = await pdfToWord(files[0], setProgress);
        setResults([{ name: `${name}.docx`, blob, type: "docx" }]);

      } else if (mode.id === "word-to-pdf") {
        setProgress("Starting…");
        const blob = await wordToPdf(files[0], setProgress);
        setResults([{ name: `${name}.pdf`, blob, type: "pdf" }]);
      }

      setStatus("done");
      const entry = {
        id: Date.now(),
        mode: mode.label,
        inputFiles: files.map(f => f.name),
        outputCount: 1,
        timestamp: Date.now(),
      };
      setHistory(h => [entry, ...h].slice(0, 30));
      onExport && onExport({ tool: "File Converter", rows: files.length, cols: 0, timestamp: Date.now() });

    } catch (err) {
      console.error(err);
      setStatus("error");
      setErrorMsg(err.message || "Conversion failed. Please try a different file.");
    }
  }, [files, mode, onExport]);

  const downloadAll = () => results.forEach(r => downloadBlob(r.blob, r.name));
  const downloadOne = (r) => downloadBlob(r.blob, r.name);
  const fileIconMap = { pdf: "📄", docx: "📝", png: "🖼", jpg: "🖼" };

  // ── Excel Editor helpers ─────────────────────────────────────────────────────
  const parseSheet = (wb, sheetName) => {
    const ws  = wb.Sheets[sheetName];
    const raw = window.XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (!raw.length) return [[]];
    const maxLen = Math.max(...raw.map(r => r.length));
    return raw.map(row =>
      Array.from({ length: maxLen }, (_, i) => String(row[i] ?? ""))
    );
  };

  const handleXlUpload = async (file) => {
    setXlStatus("loading"); setXlError(""); setXlFile(file);
    setXlEditCell(null); setXlFilters({}); setXlSort({ col: -1, dir: "asc" }); setXlSelected(new Set()); setXlColTypes({});
    try {
      await loadScript(CDN.xlsx);
      const ab = await file.arrayBuffer();
      const wb = window.XLSX.read(ab, { type: "array" });
      xlWorkbook.current = wb;
      const names = wb.SheetNames;
      setXlSheets(names);
      setXlSheet(names[0]);
      setXlData(parseSheet(wb, names[0]));
      setXlStatus("ready");
    } catch (err) {
      setXlStatus("error");
      setXlError(err.message || "Failed to read file. Make sure it is a valid .xlsx, .xls, or .csv file.");
    }
  };

  const switchXlSheet = (name) => {
    setXlSheet(name); setXlEditCell(null); setXlFilters({});
    setXlSort({ col: -1, dir: "asc" }); setXlSelected(new Set()); setXlColTypes({});
    setXlData(parseSheet(xlWorkbook.current, name));
  };

  const startXlEdit = (row, col, val) => {
    setXlEditCell({ row, col });
    setXlEditVal(String(val ?? ""));
    setTimeout(() => xlInputRef.current?.focus(), 10);
  };

  const commitXlEdit = useCallback(() => {
    setXlEditCell(prev => {
      if (!prev) return null;
      const { row, col } = prev;
      setXlData(d => d.map((r, ri) => ri === row ? r.map((c, ci) => ci === col ? xlEditVal : c) : r));
      return null;
    });
  }, [xlEditVal]);

  const toggleXlSort = (col) => {
    setXlSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" }
    );
  };

  const addXlRow = () => {
    const len = (xlData[0] || []).length;
    setXlData(prev => [...prev, Array(len).fill("")]);
  };

  const addXlCol = () => {
    setXlData(prev => prev.map((row, i) => [...row, i === 0 ? `Column ${row.length + 1}` : ""]));
  };

  const deleteXlRows = () => {
    setXlData(prev => prev.filter((_, i) => i === 0 || !xlSelected.has(i)));
    setXlSelected(new Set());
  };

  const deleteXlCol = (ci) => {
    setXlData(prev => prev.map(row => row.filter((_, j) => j !== ci)));
    setXlFilters(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = +k;
        if (ki < ci) next[ki] = v;
        else if (ki > ci) next[ki - 1] = v;
      });
      return next;
    });
    if (xlSort.col === ci) setXlSort({ col: -1, dir: "asc" });
    else if (xlSort.col > ci) setXlSort(prev => ({ ...prev, col: prev.col - 1 }));
  };

  const changeColType = (ci, newType) => {
    setXlColTypes(prev => ({ ...prev, [ci]: newType }));
    setXlData(prev => prev.map((row, ri) => {
      if (ri === 0) return row; // never transform headers
      return row.map((cell, cj) => cj === ci ? transformValue(cell, newType) : cell);
    }));
  };

  const downloadXl = async (fmt) => {
    await loadScript(CDN.xlsx);
    const wb = window.XLSX.utils.book_new();
    const ws = window.XLSX.utils.aoa_to_sheet(xlData);
    window.XLSX.utils.book_append_sheet(wb, ws, xlSheet || "Sheet1");
    const buf  = window.XLSX.write(wb, { bookType: fmt, type: "array" });
    const mime = {
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      csv:  "text/csv;charset=utf-8",
      tsv:  "text/tab-separated-values;charset=utf-8",
    };
    downloadBlob(
      new Blob([buf], { type: mime[fmt] }),
      `${stripExt(xlFile?.name || "spreadsheet")}.${fmt}`
    );
    onExport && onExport({ tool: "Excel Editor", rows: xlData.length - 1, timestamp: Date.now() });
  };

  // ── Excel derived state ──────────────────────────────────────────────────────
  const xlHeaders = xlData[0] || [];
  const xlFilteredIndexed = xlData.slice(1)
    .map((row, i) => ({ row, origIdx: i + 1 }))
    .filter(({ row }) =>
      xlHeaders.every((_, ci) => {
        const f = xlFilters[ci] || "";
        return !f || String(row[ci] ?? "").toLowerCase().includes(f.toLowerCase());
      })
    );
  const xlSortedIndexed = xlSort.col >= 0
    ? [...xlFilteredIndexed].sort((a, b) => {
        const av = String(a.row[xlSort.col] ?? "");
        const bv = String(b.row[xlSort.col] ?? "");
        return xlSort.dir === "asc"
          ? av.localeCompare(bv, undefined, { numeric: true })
          : bv.localeCompare(av, undefined, { numeric: true });
      })
    : xlFilteredIndexed;

  const hasActiveFilters = Object.values(xlFilters).some(Boolean);

  // Table cell styles
  const thSt = {
    padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 11,
    color: t.textSecondary, borderBottom: `1px solid ${t.border}`,
    borderRight: `1px solid ${t.border}`, whiteSpace: "nowrap", background: t.bgPanelHead,
  };
  const tdSt = {
    padding: "6px 10px", borderBottom: `1px solid ${t.border}`,
    borderRight: `1px solid ${t.border}`, verticalAlign: "middle", fontSize: 12,
  };

  return (
    <div>
      {/* Hero */}
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>🔄 File Converter</h2>
            <p>Convert between PDF, Word, and image formats — or edit spreadsheets. Entirely in your browser. No uploads to any server.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">{MODES.length + 1}</div><div className="tool-stat-lbl">tools</div></div>
            <div className="tool-stat">
              <div className="tool-stat-val">
                {activeTab === "Excel Editor" ? (xlData.length > 1 ? xlData.length - 1 : 0) : files.length}
              </div>
              <div className="tool-stat-lbl">{activeTab === "Excel Editor" ? "rows" : "files"}</div>
            </div>
            <div className="tool-stat"><div className="tool-stat-val">{history.length}</div><div className="tool-stat-lbl">conversions</div></div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="pill-tab-bar" style={{ display: "flex", gap: 6, padding: "12px 16px", flexWrap: "wrap" }}>
        {TABS.map(tab => (
          <button key={tab} className={`pill-tab ${activeTab === tab ? "active" : ""}`} onClick={() => setActiveTab(tab)}>
            {tab === "History" && history.length > 0 && (
              <span style={{ background: "rgba(255,255,255,0.25)", color: "inherit", borderRadius: 10, padding: "1px 6px", fontSize: 9 }}>{history.length}</span>
            )}
            {tab}
          </button>
        ))}
      </div>

      {/* ── CONVERTER TAB ── */}
      {activeTab === "Converter" && (
        <div className="tool-main">
          {/* Mode selector */}
          <div className="d1 fade-up" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginBottom: "1.25rem" }}>
            {MODES.map(m => (
              <div key={m.id} onClick={() => switchMode(m)}
                style={{
                  background: mode.id === m.id ? t.bgChipActive : t.bgPanel,
                  border: `1px solid ${mode.id === m.id ? t.borderActive : t.border}`,
                  borderRadius: 12, padding: "14px 16px", cursor: "pointer", transition: "all 0.15s",
                }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 20 }}>{m.icon}</span>
                  <span className={`pill ${m.tag}`} style={{ fontSize: 9 }}>{m.quality}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: mode.id === m.id ? t.textAccent : t.textPrimary, marginBottom: 3 }}>{m.label}</div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono" }}>{m.from} → {m.to}</div>
              </div>
            ))}
          </div>

          {/* Hint */}
          <div className="d2 fade-up" style={{ background: t.bgChipActive, border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: "1rem", fontSize: 13, color: t.textSecondary, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>💡</span>
            {mode.hint}
          </div>

          {/* Drop Zone */}
          <div className="d3 fade-up"
            style={{
              border: `2px dashed ${dragOver ? t.borderActive : files.length ? t.borderActive : t.dragBorder}`,
              borderRadius: 14, background: dragOver ? t.bgChipActive : t.dragBg,
              padding: "2rem 1.5rem", textAlign: "center", marginBottom: "1rem",
              cursor: "pointer", transition: "all 0.2s",
              color: dragOver ? t.textAccent : files.length ? t.textAccent : t.dragText,
            }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>{files.length ? "✓" : "⬆"}</div>
            {files.length ? (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{files.length} file{files.length > 1 ? "s" : ""} ready</div>
                <div style={{ fontSize: 12, color: t.textMuted }}>{files.map(f => f.name).join(", ").slice(0, 80)}</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                  Drop your <strong style={{ color: t.textAccent }}>{mode.from}</strong> file{mode.multi ? "s" : ""} here
                </div>
                <div style={{ fontSize: 12, color: t.textMuted }}>or click to browse · accepts {mode.accept.split(",").map(a => a.split("/").pop().split(".").pop()).join(", ").toUpperCase()}</div>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept={mode.accept} multiple={mode.multi} style={{ display: "none" }} onChange={e => handleFiles(e.target.files)} />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="d4 fade-up" style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: "1rem" }}>
              {files.map((f, i) => (
                <div key={i} style={{ background: t.bgPanel, border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{fileIconMap[f.name.split(".").pop()] ?? "📎"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: t.textPrimary, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono" }}>{formatBytes(f.size)}</div>
                  </div>
                  {f.size > 10 * 1024 * 1024 && <span className="pill pill-amber" style={{ fontSize: 9 }}>Large file</span>}
                  <button className="btn-sm" onClick={(e) => { e.stopPropagation(); setFiles(prev => prev.filter((_, j) => j !== i)); }} style={{ color: t.statusErr, borderColor: t.statusErr }}>✕</button>
                </div>
              ))}
              {files.length > 0 && <button className="btn-sm" onClick={resetState} style={{ alignSelf: "flex-start" }}>Clear all</button>}
            </div>
          )}

          {/* Convert button */}
          <div className="d5 fade-up" style={{ marginBottom: "1rem" }}>
            <button className="btn-primary" onClick={convert} disabled={!files.length || status === "loading"} style={{ padding: "12px 28px", fontSize: 14, gap: 10 }}>
              {status === "loading" ? "⏳ Converting…" : `🔄 Convert to ${mode.to}`}
            </button>
          </div>

          {/* Progress */}
          {status === "loading" && (
            <div style={{ background: t.bgChipActive, border: `1px solid ${t.borderActive}`, borderRadius: 10, padding: "12px 16px", marginBottom: "1rem", fontSize: 13, color: t.textAccent, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ animation: "spin 1s linear infinite", display: "inline-block", fontSize: 16 }}>⟳</span>
              {progress}
            </div>
          )}

          {/* Error */}
          {status === "error" && (
            <div style={{ background: t.borderError, border: `1px solid ${t.statusErr}`, borderRadius: 10, padding: "12px 16px", marginBottom: "1rem", fontSize: 13, color: t.statusErr }}>
              ✕ {errorMsg}
            </div>
          )}

          {/* Results */}
          {status === "done" && results.length > 0 && (
            <div className="panel d6 fade-up">
              <div className="panel-header">
                <div className="panel-title">
                  <div className="dot dot-green" />
                  Conversion Complete
                  <span className="pill pill-green" style={{ marginLeft: 4 }}>{results.length} file{results.length > 1 ? "s" : ""}</span>
                </div>
                {results.length > 1 && <button className="btn-sm" onClick={downloadAll}>↓ Download All</button>}
              </div>
              <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                {results.map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: t.bgPanelHead, borderRadius: 8, padding: "10px 12px" }}>
                    <span style={{ fontSize: 20 }}>{fileIconMap[r.type] ?? "📎"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: t.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono" }}>{formatBytes(r.blob.size)}</div>
                    </div>
                    <button className="btn-primary" onClick={() => downloadOne(r)} style={{ padding: "6px 16px", fontSize: 12 }}>↓ Download</button>
                  </div>
                ))}
              </div>
              <div className="status-bar ok">✓ All files ready to download · {mode.from} → {mode.to} conversion complete</div>
            </div>
          )}

          <div style={{ marginTop: "1.5rem", padding: "12px 14px", background: t.bgPanel, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 12, color: t.textMuted, lineHeight: 1.7 }}>
            <strong style={{ color: t.textSecondary }}>How it works:</strong> All conversions run entirely in your browser using JavaScript libraries. No file is ever uploaded to a server.
            PDF → Word extracts text only (formatting may vary). Word → PDF renders text and basic styles. Images → PDF and PDF → Images are pixel-perfect.
          </div>
        </div>
      )}

      {/* ── EXCEL EDITOR TAB ── */}
      {activeTab === "Excel Editor" && (
        <div className="tool-main">

          {/* Upload zone */}
          {xlStatus === "idle" && (
            <div
              style={{
                border: `2px dashed ${xlDrag ? t.borderActive : t.dragBorder}`,
                borderRadius: 14, background: xlDrag ? t.bgChipActive : t.dragBg,
                padding: "3rem 1.5rem", textAlign: "center", cursor: "pointer",
                transition: "all 0.2s", color: xlDrag ? t.textAccent : t.dragText,
              }}
              onDragOver={e => { e.preventDefault(); setXlDrag(true); }}
              onDragLeave={() => setXlDrag(false)}
              onDrop={e => { e.preventDefault(); setXlDrag(false); const f = e.dataTransfer.files[0]; if (f) handleXlUpload(f); }}
              onClick={() => xlFileRef.current?.click()}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>📊</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Drop your spreadsheet here</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 16 }}>Accepts .xlsx · .xls · .csv — click to browse</div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                {[".xlsx", ".xls", ".csv"].map(ext => (
                  <span key={ext} style={{ background: t.bgPanel, border: `1px solid ${t.border}`, borderRadius: 6, padding: "3px 10px", fontSize: 11, color: t.textSecondary, fontFamily: "JetBrains Mono" }}>{ext}</span>
                ))}
              </div>
              <input ref={xlFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }}
                onChange={e => { const f = e.target.files[0]; if (f) handleXlUpload(f); e.target.value = ""; }} />
            </div>
          )}

          {/* Loading */}
          {xlStatus === "loading" && (
            <div style={{ textAlign: "center", padding: "4rem", color: t.textSecondary }}>
              <div style={{ fontSize: 36, marginBottom: 14, animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</div>
              <div style={{ fontSize: 14 }}>Reading spreadsheet…</div>
            </div>
          )}

          {/* Error */}
          {xlStatus === "error" && (
            <div style={{ background: t.borderError, border: `1px solid ${t.statusErr}`, borderRadius: 10, padding: "16px 20px", color: t.statusErr, display: "flex", alignItems: "center", gap: 12 }}>
              <span>✕ {xlError}</span>
              <button className="btn-sm" onClick={() => { setXlStatus("idle"); setXlError(""); setXlFile(null); }}>Try again</button>
            </div>
          )}

          {/* Editor */}
          {xlStatus === "ready" && (
            <>
              {/* Toolbar */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
                {/* File chip */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: t.bgPanel, border: `1px solid ${t.border}`, borderRadius: 8, padding: "5px 10px", fontSize: 12 }}>
                  <span>📊</span>
                  <span style={{ color: t.textSecondary, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{xlFile?.name}</span>
                  <button className="btn-sm" style={{ padding: "2px 7px", fontSize: 10, color: t.statusErr, borderColor: t.statusErr }}
                    onClick={() => { setXlStatus("idle"); setXlFile(null); setXlData([]); setXlSheets([]); setXlFilters({}); setXlSelected(new Set()); }}>✕</button>
                </div>

                {/* Sheet selector */}
                {xlSheets.length > 1 && (
                  <select value={xlSheet} onChange={e => switchXlSheet(e.target.value)}
                    style={{ background: t.bgPanel, border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, color: t.textPrimary, cursor: "pointer" }}>
                    {xlSheets.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}

                {/* Row/col actions */}
                <button className="btn-sm" onClick={addXlRow}>＋ Row</button>
                <button className="btn-sm" onClick={addXlCol}>＋ Column</button>
                {xlSelected.size > 0 && (
                  <button className="btn-sm" style={{ color: t.statusErr, borderColor: t.statusErr }} onClick={deleteXlRows}>
                    ✕ Delete {xlSelected.size} row{xlSelected.size !== 1 ? "s" : ""}
                  </button>
                )}
                {hasActiveFilters && (
                  <button className="btn-sm" onClick={() => setXlFilters({})}>✕ Clear filters</button>
                )}

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Download */}
                <span style={{ fontSize: 11, color: t.textMuted }}>Download as:</span>
                {["xlsx", "csv", "tsv"].map(fmt => (
                  <button key={fmt} className="btn-sm" style={{ fontFamily: "JetBrains Mono", fontSize: 10, padding: "5px 10px", background: t.bgChipActive, borderColor: t.borderActive, color: t.textAccent }}
                    onClick={() => downloadXl(fmt)}>↓ {fmt.toUpperCase()}</button>
                ))}
              </div>

              {/* Stats bar */}
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span>{xlHeaders.length} columns</span>
                <span>{xlData.length - 1} rows</span>
                {hasActiveFilters && <span style={{ color: t.textAccent }}>● {xlSortedIndexed.length} visible after filter</span>}
                {xlSort.col >= 0 && <span style={{ color: t.textAccent }}>● sorted by "{xlHeaders[xlSort.col]}" {xlSort.dir === "asc" ? "↑" : "↓"}</span>}
                {xlSelected.size > 0 && <span style={{ color: t.textAccent }}>● {xlSelected.size} selected</span>}
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 520, border: `1px solid ${t.border}`, borderRadius: 10 }}>
                <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
                  <thead>
                    {/* Header row */}
                    <tr style={{ position: "sticky", top: 0, zIndex: 3 }}>
                      <th style={{ ...thSt, width: 36, textAlign: "center", padding: "8px 6px" }}>
                        <input type="checkbox"
                          checked={xlSelected.size > 0 && xlSelected.size === xlData.length - 1}
                          onChange={e => setXlSelected(e.target.checked
                            ? new Set(xlData.slice(1).map((_, i) => i + 1))
                            : new Set()
                          )} />
                      </th>
                      <th style={{ ...thSt, width: 40, color: t.textMuted, fontSize: 10, textAlign: "right", paddingRight: 8 }}>#</th>
                      {xlHeaders.map((h, ci) => (
                        <th key={ci} style={{ ...thSt, minWidth: 100, cursor: "pointer", position: "relative" }}
                          onClick={() => { if (!xlEditCell || xlEditCell.row !== 0 || xlEditCell.col !== ci) toggleXlSort(ci); }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, minHeight: 22 }}>
                            {xlEditCell?.row === 0 && xlEditCell?.col === ci ? (
                              <input ref={xlInputRef} value={xlEditVal}
                                onChange={e => setXlEditVal(e.target.value)}
                                onBlur={commitXlEdit}
                                onKeyDown={e => { if (e.key === "Enter") commitXlEdit(); if (e.key === "Escape") setXlEditCell(null); e.stopPropagation(); }}
                                onClick={e => e.stopPropagation()}
                                style={{ flex: 1, background: "transparent", border: "none", outline: `2px solid ${t.borderActive}`, borderRadius: 3, color: t.textPrimary, fontWeight: 700, fontSize: 11, padding: "2px 4px", fontFamily: "inherit" }} />
                            ) : (
                              <span style={{ flex: 1, fontWeight: 700, color: t.textPrimary, userSelect: "none" }}
                                onDoubleClick={e => { e.stopPropagation(); startXlEdit(0, ci, h); }}>
                                {h || <span style={{ color: t.textMuted, fontWeight: 400 }}>Column {ci + 1}</span>}
                              </span>
                            )}
                            {xlSort.col === ci && <span style={{ color: t.textAccent, fontSize: 10, flexShrink: 0 }}>{xlSort.dir === "asc" ? "↑" : "↓"}</span>}
                            <span title="Delete column"
                              style={{ color: t.textMuted, fontSize: 11, cursor: "pointer", flexShrink: 0, opacity: 0.5, lineHeight: 1 }}
                              onClick={e => { e.stopPropagation(); deleteXlCol(ci); }}
                              onMouseOver={e => e.currentTarget.style.opacity = "1"}
                              onMouseOut={e => e.currentTarget.style.opacity = "0.5"}>×</span>
                          </div>
                        </th>
                      ))}
                      {/* Add column button */}
                      <th style={{ ...thSt, width: 36, textAlign: "center", padding: "6px" }}>
                        <button title="Add column"
                          style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 16, padding: "0 4px", lineHeight: 1 }}
                          onClick={addXlCol}>＋</button>
                      </th>
                    </tr>

                    {/* Type selector row */}
                    <tr style={{ position: "sticky", top: 40, zIndex: 2, background: t.bgPanel }}>
                      <td style={{ ...tdSt, padding: "3px 6px" }} />
                      <td style={{ ...tdSt, padding: "3px 6px" }} />
                      {xlHeaders.map((_, ci) => {
                        const currentType = xlColTypes[ci] || "text";
                        const typeDef = TYPE_DEFS.find(td => td.id === currentType);
                        return (
                          <td key={ci} style={{ ...tdSt, padding: "3px 6px" }}>
                            <select
                              value={currentType}
                              onChange={e => changeColType(ci, e.target.value)}
                              onClick={e => e.stopPropagation()}
                              title="Change column data type — transforms all values"
                              style={{
                                width: "100%", boxSizing: "border-box",
                                background: t.bgInput,
                                border: `1px solid ${currentType !== "text" ? typeDef?.color : t.border}`,
                                borderRadius: 5, padding: "2px 5px",
                                fontSize: 10, fontFamily: "JetBrains Mono", fontWeight: 700,
                                color: typeDef?.color || t.textMuted,
                                cursor: "pointer", outline: "none",
                              }}>
                              {TYPE_DEFS.map(td => (
                                <option key={td.id} value={td.id}>{td.icon}  {td.label}</option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                      <td style={{ ...tdSt, padding: "3px 6px" }} />
                    </tr>

                    {/* Filter row */}
                    <tr style={{ position: "sticky", top: 74, zIndex: 2, background: t.bgPanel }}>
                      <td style={{ ...tdSt, padding: "4px 6px" }} />
                      <td style={{ ...tdSt, padding: "4px 6px" }} />
                      {xlHeaders.map((_, ci) => (
                        <td key={ci} style={{ ...tdSt, padding: "4px 6px" }}>
                          <input
                            placeholder="Filter…"
                            value={xlFilters[ci] || ""}
                            onChange={e => setXlFilters(prev => ({ ...prev, [ci]: e.target.value }))}
                            style={{
                              width: "100%", boxSizing: "border-box",
                              background: t.bgInput, border: `1px solid ${xlFilters[ci] ? t.borderActive : t.border}`,
                              borderRadius: 5, padding: "3px 7px", fontSize: 11,
                              color: t.textPrimary, outline: "none", fontFamily: "inherit",
                            }} />
                        </td>
                      ))}
                      <td style={{ ...tdSt, padding: "4px 6px" }} />
                    </tr>
                  </thead>

                  <tbody>
                    {xlSortedIndexed.length === 0 ? (
                      <tr>
                        <td colSpan={xlHeaders.length + 3}
                          style={{ textAlign: "center", padding: "2.5rem", color: t.textMuted, fontSize: 13 }}>
                          No rows match your filters.
                          <button className="btn-sm" style={{ marginLeft: 10 }} onClick={() => setXlFilters({})}>Clear filters</button>
                        </td>
                      </tr>
                    ) : (
                      xlSortedIndexed.map(({ row, origIdx }, di) => (
                        <tr key={origIdx}
                          style={{ background: xlSelected.has(origIdx) ? t.bgChipActive : di % 2 === 1 ? t.bgPanel + "55" : "transparent" }}>
                          <td style={{ ...tdSt, textAlign: "center", width: 36, padding: "5px 6px" }}>
                            <input type="checkbox"
                              checked={xlSelected.has(origIdx)}
                              onChange={e => {
                                setXlSelected(prev => {
                                  const next = new Set(prev);
                                  e.target.checked ? next.add(origIdx) : next.delete(origIdx);
                                  return next;
                                });
                              }} />
                          </td>
                          <td style={{ ...tdSt, color: t.textMuted, fontSize: 10, textAlign: "right", paddingRight: 8, userSelect: "none", width: 40 }}>{origIdx}</td>
                          {row.map((cell, ci) => (
                            <td key={ci} style={{ ...tdSt, cursor: "text", maxWidth: 220 }}
                              onClick={() => startXlEdit(origIdx, ci, cell)}>
                              {xlEditCell?.row === origIdx && xlEditCell?.col === ci ? (
                                <input ref={xlInputRef} value={xlEditVal}
                                  onChange={e => setXlEditVal(e.target.value)}
                                  onBlur={commitXlEdit}
                                  onKeyDown={e => {
                                    if (e.key === "Enter") commitXlEdit();
                                    if (e.key === "Escape") setXlEditCell(null);
                                    if (e.key === "Tab") { e.preventDefault(); commitXlEdit(); startXlEdit(origIdx, Math.min(ci + 1, xlHeaders.length - 1), row[Math.min(ci + 1, xlHeaders.length - 1)]); }
                                  }}
                                  style={{ width: "100%", minWidth: 80, background: "transparent", border: "none", outline: `2px solid ${t.borderActive}`, borderRadius: 3, color: t.textPrimary, fontSize: 12, padding: "2px 4px", fontFamily: "inherit" }} />
                              ) : (
                                <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200, color: t.textPrimary }}>
                                  {String(cell ?? "")}
                                </span>
                              )}
                            </td>
                          ))}
                          {/* Row delete */}
                          <td style={{ ...tdSt, width: 36, textAlign: "center", padding: "5px 4px" }}>
                            <button title="Delete row"
                              style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 12, padding: "2px 6px", borderRadius: 4 }}
                              onMouseOver={e => e.currentTarget.style.color = t.statusErr}
                              onMouseOut={e => e.currentTarget.style.color = t.textMuted}
                              onClick={() => setXlData(prev => prev.filter((_, i) => i !== origIdx))}>✕</button>
                          </td>
                        </tr>
                      ))
                    )}
                    {/* Add row button row */}
                    <tr>
                      <td colSpan={xlHeaders.length + 3} style={{ padding: "6px 10px", borderTop: `1px solid ${t.border}` }}>
                        <button className="btn-sm" onClick={addXlRow} style={{ fontSize: 11 }}>＋ Add row</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Tips */}
              <div style={{ marginTop: 10, fontSize: 11, color: t.textMuted, lineHeight: 1.8 }}>
                <strong style={{ color: t.textSecondary }}>Tips:</strong> Click any cell to edit · Tab to move right · Double-click a header to rename it · Click header to sort · Use filter row to search per column · Select rows with checkboxes then delete in bulk
              </div>
            </>
          )}
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {activeTab === "History" && (
        <div className="tool-main">
          {history.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem 1rem", color: t.textMuted }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔄</div>
              <div style={{ fontSize: 14, marginBottom: 6 }}>No conversions yet this session</div>
              <div style={{ fontSize: 12 }}>Each conversion will be logged here</div>
              <button className="btn-sm" style={{ marginTop: 16 }} onClick={() => setActiveTab("Converter")}>← Back to Converter</button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                <div style={{ fontSize: 13, color: t.textSecondary }}>
                  <span style={{ color: t.textAccent, fontWeight: 600 }}>{history.length}</span> conversion{history.length !== 1 ? "s" : ""} this session
                </div>
                <button className="btn-sm" onClick={() => setHistory([])}>Clear All</button>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {history.map(item => (
                  <div key={item.id} className="panel" style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: t.bgChipActive, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🔄</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: t.textPrimary, marginBottom: 3 }}>{item.mode}</div>
                      <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.inputFiles.join(", ")} · {new Date(item.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <button className="btn-sm" onClick={() => setHistory(h => h.filter(x => x.id !== item.id))} style={{ color: t.statusErr, borderColor: t.statusErr }}>✕</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input[type="checkbox"] { cursor: pointer; accent-color: ${t.textAccent}; }
      `}</style>

      <article className="tool-seo-section" aria-label="About File Converter">
        <h2>About File Converter</h2>
        <div className="tool-seo-cols">
          <div>
            <h3>How it works</h3>
            <ul>
              <li>PDF → Word extracts text from each PDF page using PDF.js and rebuilds a DOCX structure.</li>
              <li>Word → PDF renders DOCX content with styled headings and paragraphs via jsPDF.</li>
              <li>Images → PDF places each image on its own page using jsPDF's image embedding.</li>
              <li>PDF → Images renders each page at high DPI using PDF.js and a canvas element.</li>
              <li>Excel Editor reads .xlsx/.xls/.csv via SheetJS — edit cells inline, filter, sort, and export.</li>
              <li>All libraries load from a CDN on first use — no files are ever uploaded.</li>
            </ul>
          </div>
          <div>
            <h3>Use cases</h3>
            <ul>
              <li>Extract editable text from PDF reports.</li>
              <li>Combine scanned images into a single PDF for sharing.</li>
              <li>Convert Word documents to PDF for consistent rendering.</li>
              <li>Extract individual pages from a PDF as PNG images.</li>
              <li>Open a CSV or Excel file, clean data, add columns, and re-export.</li>
            </ul>
          </div>
          <div>
            <h3>Privacy</h3>
            <ul>
              <li>Files are processed entirely in your browser.</li>
              <li>No document or spreadsheet content is sent to any server.</li>
              <li>Free to use with no sign-up or account required.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ marginTop: "1.5rem" }}>Frequently Asked Questions</h2>
        <details><summary>How accurate is the PDF to Word conversion?</summary><p>The converter extracts plain text from each PDF page. Formatting (fonts, images, tables, columns) is not fully preserved because PDF is a layout format rather than a document format. For text-heavy PDFs, the output is typically clean and editable.</p></details>
        <details><summary>Como converter PDF para PNG gratuitamente? (How to convert PDF to PNG for free?)</summary><p>Use the PDF → Images tab. Upload your PDF and each page is rendered as a high-quality PNG using PDF.js. Click "Download" for each page. Everything runs in your browser — no uploads, no sign-up, completely free.</p></details>
        <details><summary>Can I convert multiple images to a single PDF?</summary><p>Yes. Select the "Images → PDF" mode and add multiple JPEG, PNG, or WebP images. Each image is placed on its own page. Click "Create PDF" to generate the combined file for download.</p></details>
        <details><summary>Does miniDevToolkit upload my files during conversion?</summary><p>No. All conversion happens in your browser using PDF.js, jsPDF, docx, mammoth.js, and SheetJS — loaded from public CDNs once. Your document content is never sent to any server.</p></details>
        <details><summary>What can I do with the Excel Editor?</summary><p>Upload any .xlsx, .xls, or .csv file to view it as an editable table. Click any cell to edit it inline, double-click a column header to rename it, use the filter row to search within columns, click headers to sort, select rows with checkboxes to delete in bulk, add or remove rows and columns, and download the result as XLSX, CSV, or TSV.</p></details>
        <h2 style={{ marginTop: "1.5rem" }}>Alternatives &amp; why miniDevToolkit</h2>
        <p>Compared to <strong>ILovePDF</strong> and <strong>Smallpdf</strong> — these services upload your files to their servers for processing. miniDevToolkit converts entirely in your browser — your documents never leave your device. Compared to <strong>Adobe Acrobat online</strong> — miniDevToolkit requires no account or subscription. The Excel Editor is a lightweight alternative to uploading spreadsheets to Google Sheets or Excel Online — your data stays private.</p>
      </article>
    </div>
  );
}
